import React, { useState, useEffect } from 'react';
import * as LucideIcons from 'lucide-react';
import './Products.css';

const Products = () => {
  const formatINR = (value) => {
    const num = Number(value || 0);
    try {
      return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(num);
    } catch (_) {
      return `₹${num.toFixed(2)}`;
    }
  };
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [products, setProducts] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'table'
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkData, setBulkData] = useState({ priceDeltaPercent: '', newPrice: '', stockDelta: '', newStock: '' });
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showCSVModal, setShowCSVModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [csvFile, setCsvFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showDetails, setShowDetails] = useState(false);
  const [detailsProduct, setDetailsProduct] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    sku: '',
    category: '',
    category_id: '',
    price: '',
    cost_price: '',
    image_url: '',
    product_images: [], // Array for multiple images
    unit: 'piece',
    weight: '',
    hsn_code: '',
    gst_slab: '',
    stock_on_hand: 0,
    min_stock_level: 0,
    reorder_point: 0,
    reorder_quantity: 0,
    vendor_id: ''
  });
  const [uploadingImages, setUploadingImages] = useState(false);
  const [imagePreview, setImagePreview] = useState([]);
  const [showPriceRequest, setShowPriceRequest] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [priceRequestProduct, setPriceRequestProduct] = useState(null);
  const [requestedPrice, setRequestedPrice] = useState('');
  const [requestReason, setRequestReason] = useState('');
  const [vendors, setVendors] = useState([]);
  const [categories, setCategories] = useState([]);
  const [gstRates, setGstRates] = useState([]);

  // Fetch products from API
  const fetchProducts = async () => {
    try {
      setLoading(true);
      let token = localStorage.getItem('token');
      let adminApiKey = localStorage.getItem('adminApiKey');
      const userType = localStorage.getItem('userType');
      
      // Only treat as admin when userType is admin AND adminApiKey is present
      const isAdmin = userType === 'admin' && Boolean(adminApiKey);
      
      if (!token && !isAdmin) {
        try {
          const v = JSON.parse(localStorage.getItem('vendorData') || 'null');
          token = v?.token || '';
        } catch (_) {}
      }
      
      const headers = {};
      if (isAdmin && adminApiKey) {
        headers['x-admin-key'] = adminApiKey;
      } else if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const endpoint = isAdmin ? '/api/admin/products' : '/api/products';
      const response = await fetch(`${endpoint}?page=${currentPage}&limit=10000&search=${encodeURIComponent(searchQuery)}&category=${encodeURIComponent(categoryFilter)}&status=${encodeURIComponent(statusFilter)}`, {
        headers
      });
      
      if (response.ok) {
        const data = await response.json();
        let products = data.products;
        
        // For vendors, fetch final prices for their products
        if (!isAdmin && products.length > 0) {
          const pricePromises = products.map(async (product) => {
            try {
              const priceResponse = await fetch(`/api/products/${product.id}/price`, {
                headers: { 'Authorization': `Bearer ${token}` }
              });
              if (priceResponse.ok) {
                const priceData = await priceResponse.json();
                return { ...product, final_price: priceData.final_price };
              }
            } catch (error) {
              console.error(`Error fetching price for product ${product.id}:`, error);
            }
            return product;
          });
          
          products = await Promise.all(pricePromises);
        }
        
        setProducts(products);
        setTotalPages(data.totalPages);
      } else if (response.status === 401) {
        alert('Your session has expired. Please log in again.');
        try { localStorage.removeItem('token'); localStorage.removeItem('vendorData'); localStorage.setItem('isLoggedIn','false'); } catch (_) {}
        window.location.reload();
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch vendors for admin users
  const fetchVendors = async () => {
    try {
      const adminApiKey = localStorage.getItem('adminApiKey');
      const isAdmin = localStorage.getItem('userType') === 'admin' && Boolean(adminApiKey);
      
      if (isAdmin) {
        const response = await fetch('/api/admin/vendors', {
          headers: {
            'x-admin-key': adminApiKey
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          setVendors(data.vendors || []);
        }
      }
    } catch (error) {
      console.error('Error fetching vendors:', error);
    }
  };

  // Fetch categories from API
  const fetchCategories = async () => {
    try {
      const response = await fetch('/api/products/categories');
      if (response.ok) {
        const data = await response.json();
        setCategories(data.categories || []);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  // Fetch GST rates from API
  const fetchGstRates = async () => {
    try {
      const response = await fetch('/api/products/gst-rates');
      if (response.ok) {
        const data = await response.json();
        setGstRates(data.gstRates || []);
      }
    } catch (error) {
      console.error('Error fetching GST rates:', error);
    }
  };

  // Helper to provide human-friendly descriptions for GST rates
  const describeGstRate = (gstValue) => {
    switch (Number(gstValue)) {
      case 0:
        return 'Exempt';
      case 3:
        return 'Precious Metals';
      case 5:
        return 'Essential Items';
      case 12:
        return 'Standard Rate';
      case 18:
        return 'Standard Rate';
      case 28:
        return 'Luxury Items';
      default:
        return 'Other';
    }
  };

  // Fallback: derive GST rates from categories if API returned none
  useEffect(() => {
    if (Array.isArray(gstRates) && gstRates.length > 0) return;
    if (!Array.isArray(categories) || categories.length === 0) return;

    try {
      const uniqueRatesSet = new Set();
      categories.forEach((cat) => {
        const r = cat && cat.gst_rate;
        if (r === null || r === undefined || r === '') return;
        const num = Number(parseFloat(r));
        if (!isNaN(num)) uniqueRatesSet.add(num);
      });
      const uniqueRates = Array.from(uniqueRatesSet).sort((a, b) => a - b);
      if (uniqueRates.length > 0) {
        const derived = uniqueRates.map((rate) => ({
          rate,
          description: describeGstRate(rate),
          display: `${rate}% - ${describeGstRate(rate)}`
        }));
        setGstRates(derived);
      }
    } catch (e) {
      // Safe no-op; keep dropdown empty if derivation fails
    }
  }, [categories, gstRates]);

  useEffect(() => {
    fetchProducts();
    fetchVendors();
    fetchCategories();
    fetchGstRates();
  }, [currentPage, searchQuery, categoryFilter, statusFilter]);

  // Real-time validation for individual fields
  const validateField = (name, value) => {
    const errors = {};
    
    switch (name) {
      case 'name':
        if (!value.trim()) {
          errors.name = 'Product name is required';
        } else if (value.trim().length < 2) {
          errors.name = 'Product name must be at least 2 characters';
        }
        break;
        
      case 'sku':
        if (!value.trim()) {
          errors.sku = 'SKU is required';
        } else if (value.trim().length < 3) {
          errors.sku = 'SKU must be at least 3 characters';
        }
        break;
        
      case 'cost_price':
        if (!value || value === '') {
          errors.cost_price = 'Cost price is required';
        } else if (isNaN(value) || Number(value) <= 0) {
          errors.cost_price = 'Cost price must be a positive number';
        }
        break;
        
      case 'hsn_code':
        const gstCategories = ['Food & Beverages', 'Dairy', 'Bakery', 'Pantry'];
        if (gstCategories.includes(formData.category) && !value.trim()) {
          errors.hsn_code = 'HSN code is required for the selected category';
        } else if (value.trim() && !/^\d{3,5}$/.test(value.trim())) {
          errors.hsn_code = 'HSN code must be 3-5 digits';
        }
        break;
        
      case 'gst_slab':
        if (value && (isNaN(value) || Number(value) < 0 || Number(value) > 28)) {
          errors.gst_slab = 'GST slab must be between 0% and 28%';
        }
        break;
        
      case 'stock_on_hand':
        if (value !== '' && (isNaN(value) || Number(value) < 0)) {
          errors.stock_on_hand = 'Stock on hand must be a non-negative number';
        }
        break;
        
      case 'min_stock_level':
        if (value !== '' && (isNaN(value) || Number(value) < 0)) {
          errors.min_stock_level = 'Min stock level must be a non-negative number';
        }
        break;
        
      case 'reorder_point':
        if (value !== '' && (isNaN(value) || Number(value) < 0)) {
          errors.reorder_point = 'Reorder point must be a non-negative number';
        }
        break;
        
      case 'reorder_quantity':
        if (value !== '' && (isNaN(value) || Number(value) < 0)) {
          errors.reorder_quantity = 'Reorder quantity must be a non-negative number';
        }
        break;
        
      case 'category':
        if (!value) {
          errors.category = 'Category is required';
        }
        break;
        
      case 'vendor_id':
        if (!value) {
          errors.vendor_id = 'Please select a vendor for this product';
        }
        break;
        
      case 'unit':
        if (!value) {
          errors.unit = 'Unit is required';
        }
        break;
        
      case 'stock_on_hand':
        if (value === '' || value === null || value === undefined) {
          errors.stock_on_hand = 'Stock on hand is required';
        } else if (isNaN(value) || Number(value) < 0) {
          errors.stock_on_hand = 'Stock on hand must be a non-negative number';
        }
        break;
        
      case 'min_stock_level':
        if (value === '' || value === null || value === undefined) {
          errors.min_stock_level = 'Min stock level is required';
        } else if (isNaN(value) || Number(value) < 0) {
          errors.min_stock_level = 'Min stock level must be a non-negative number';
        }
        break;
        
      case 'reorder_point':
        if (value === '' || value === null || value === undefined) {
          errors.reorder_point = 'Reorder point is required';
        } else if (isNaN(value) || Number(value) < 0) {
          errors.reorder_point = 'Reorder point must be a non-negative number';
        }
        break;
        
      case 'reorder_quantity':
        if (value === '' || value === null || value === undefined) {
          errors.reorder_quantity = 'Reorder quantity is required';
        } else if (isNaN(value) || Number(value) < 0) {
          errors.reorder_quantity = 'Reorder quantity must be a non-negative number';
        }
        break;
        
      case 'weight':
        if (!value || value.trim() === '') {
          errors.weight = 'Weight is required';
        }
        break;
    }
    
    return errors;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Real-time validation
    const fieldErrors = validateField(name, value);
    setValidationErrors(prev => ({
      ...prev,
      [name]: fieldErrors[name] || '', // Clear error if field is now valid
      ...fieldErrors
    }));
  };

  // Handle category change with automatic GST, HSN, and SKU population
  const handleCategoryChange = async (e) => {
    const { value } = e.target;
    
    // Clear validation error for category field
    if (validationErrors.category) {
      setValidationErrors(prev => ({
        ...prev,
        category: ''
      }));
    }

    if (value) {
      // Find the selected category to get GST and HSN data
      const selectedCategory = categories.find(cat => cat.name === value);
      
      if (selectedCategory) {
        // Get vendor ID from session
        let vendorId = null;
        const userType = localStorage.getItem('userType');
        const isAdmin = userType === 'admin' && Boolean(localStorage.getItem('adminApiKey'));
        
        if (!isAdmin) {
          // For vendors, get vendor_id from vendorData (format: VDR001, VDR002, etc.)
          try {
            const vendorData = JSON.parse(localStorage.getItem('vendorData') || 'null');
            vendorId = vendorData?.vendor_id || vendorData?.id; // Use vendor_id if available, fallback to id
          } catch (error) {
            console.error('Error parsing vendor data:', error);
          }
        } else {
          // For admin, use the selected vendor_id from form
          vendorId = formData.vendor_id;
        }

        // Generate category code (first 3 letters of category name, uppercase)
        const categoryCode = value.substring(0, 3).toUpperCase();
        
        // Generate SKU using the backend endpoint for proper sequencing
        let sku = '';
        if (vendorId) {
          try {
            const response = await fetch(`/api/products/next-sku/${vendorId}/${categoryCode}`);
            if (response.ok) {
              const data = await response.json();
              sku = data.nextSku;
            } else {
              // Fallback to basic SKU if API fails
              sku = `SONI-${vendorId}-${categoryCode}-0001`;
            }
          } catch (error) {
            console.error('Error fetching next SKU:', error);
            // Fallback to basic SKU if API fails
            sku = `SONI-${vendorId}-${categoryCode}-0001`;
          }
        }

        // Auto-populate GST, HSN, and SKU
        const gstValue = selectedCategory.gst_rate !== undefined && selectedCategory.gst_rate !== null && selectedCategory.gst_rate !== ''
          ? String(parseFloat(selectedCategory.gst_rate))
          : '';
        
        setFormData(prev => ({
          ...prev,
          category: value,
          category_id: selectedCategory.id ?? '',
          gst_slab: gstValue,
          hsn_code: selectedCategory.hsn_code || '',
          sku: sku
        }));
        
        // Trigger real-time validation for updated fields
        const updatedFormData = {
          ...formData,
          category: value,
          category_id: selectedCategory.id ?? '',
          gst_slab: gstValue,
          hsn_code: selectedCategory.hsn_code || '',
          sku: sku
        };
        
        // Validate updated fields
        const fieldErrors = {};
        const categoryErrors = validateField('category', value);
        const gstErrors = validateField('gst_slab', gstValue);
        const hsnErrors = validateField('hsn_code', selectedCategory.hsn_code || '');
        const skuErrors = validateField('sku', sku);
        
        Object.assign(fieldErrors, categoryErrors, gstErrors, hsnErrors, skuErrors);
        
        setValidationErrors(prev => ({
          ...prev,
          category: fieldErrors.category || '',
          gst_slab: fieldErrors.gst_slab || '',
          hsn_code: fieldErrors.hsn_code || '',
          sku: fieldErrors.sku || '',
          ...fieldErrors
        }));
      }
    } else {
      // Clear related fields when category is deselected
        setFormData(prev => ({
          ...prev,
          category: value,
          category_id: '',
          gst_slab: '',
          hsn_code: '',
          sku: ''
        }));
      
      // Clear validation errors for cleared fields and validate category
      const categoryErrors = validateField('category', value);
      setValidationErrors(prev => ({
        ...prev,
        category: categoryErrors.category || '',
        gst_slab: '',
        hsn_code: '',
        sku: ''
      }));
    }
  };

  const validateForm = () => {
    const errors = {};
    
    // Required field validations
    if (!formData.name.trim()) {
      errors.name = 'Product name is required';
    } else if (formData.name.trim().length < 2) {
      errors.name = 'Product name must be at least 2 characters';
    }
    
    if (!formData.sku.trim()) {
      errors.sku = 'SKU is required';
    } else if (formData.sku.trim().length < 3) {
      errors.sku = 'SKU must be at least 3 characters';
    }
    
    if (!formData.category) {
      errors.category = 'Category is required';
    }
    
    if (!formData.cost_price || formData.cost_price === '') {
      errors.cost_price = 'Cost price is required';
    } else if (isNaN(formData.cost_price) || Number(formData.cost_price) <= 0) {
      errors.cost_price = 'Cost price must be a positive number';
    }
    
    // HSN code validation for GST categories
    const gstCategories = ['Food & Beverages', 'Dairy', 'Bakery', 'Pantry'];
    if (gstCategories.includes(formData.category) && !formData.hsn_code.trim()) {
      errors.hsn_code = 'HSN code is required for the selected category';
    } else if (formData.hsn_code.trim() && !/^\d{3,5}$/.test(formData.hsn_code.trim())) {
      errors.hsn_code = 'HSN code must be 3-5 digits';
    }
    
    // GST slab validation
    if (formData.gst_slab && (isNaN(formData.gst_slab) || Number(formData.gst_slab) < 0 || Number(formData.gst_slab) > 28)) {
      errors.gst_slab = 'GST slab must be between 0% and 28%';
    }
    
    // Unit validation
    if (!formData.unit) {
      errors.unit = 'Unit is required';
    }
    
    // Weight validation
    if (!formData.weight || formData.weight.trim() === '') {
      errors.weight = 'Weight is required';
    }
    
    // Stock validations - now required fields
    if (formData.stock_on_hand === '' || formData.stock_on_hand === null || formData.stock_on_hand === undefined) {
      errors.stock_on_hand = 'Stock on hand is required';
    } else if (isNaN(formData.stock_on_hand) || Number(formData.stock_on_hand) < 0) {
      errors.stock_on_hand = 'Stock on hand must be a non-negative number';
    }
    
    if (formData.min_stock_level === '' || formData.min_stock_level === null || formData.min_stock_level === undefined) {
      errors.min_stock_level = 'Min stock level is required';
    } else if (isNaN(formData.min_stock_level) || Number(formData.min_stock_level) < 0) {
      errors.min_stock_level = 'Min stock level must be a non-negative number';
    }
    
    if (formData.reorder_point === '' || formData.reorder_point === null || formData.reorder_point === undefined) {
      errors.reorder_point = 'Reorder point is required';
    } else if (isNaN(formData.reorder_point) || Number(formData.reorder_point) < 0) {
      errors.reorder_point = 'Reorder point must be a non-negative number';
    }
    
    if (formData.reorder_quantity === '' || formData.reorder_quantity === null || formData.reorder_quantity === undefined) {
      errors.reorder_quantity = 'Reorder quantity is required';
    } else if (isNaN(formData.reorder_quantity) || Number(formData.reorder_quantity) < 0) {
      errors.reorder_quantity = 'Reorder quantity must be a non-negative number';
    }
    
    // Admin validation
    const isAdmin = (localStorage.getItem('userType') === 'admin') && Boolean(localStorage.getItem('adminApiKey'));
    if (isAdmin && !formData.vendor_id) {
      errors.vendor_id = 'Please select a vendor for this product';
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const openAddProduct = () => {
    setEditingProduct(null);
    setFormData({
      name: '',
      description: '',
      sku: '',
      category: '',
      category_id: '',
      price: '',
      cost_price: '',
      image_url: '',
      product_images: [],
      unit: 'piece',
      weight: '',
      hsn_code: '',
      gst_slab: '',
      stock_on_hand: 0,
      min_stock_level: 0,
      reorder_point: 0,
      reorder_quantity: 0,
      vendor_id: ''
    });
    setValidationErrors({});
    setImagePreview([]);
    setShowModal(true);
  };

  // Handle image upload
  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploadingImages(true);
    try {
      const uploadPromises = files.map(async (file) => {
        const formData = new FormData();
        formData.append('image', file);
        
        let token = localStorage.getItem('token');
        let adminApiKey = localStorage.getItem('adminApiKey');
        const isAdmin = localStorage.getItem('userType') === 'admin' && Boolean(adminApiKey);
        
        if (!token && !isAdmin) {
          try {
            const v = JSON.parse(localStorage.getItem('vendorData') || 'null');
            token = v?.token || '';
          } catch (_) {}
        }
        
        const headers = {};
        if (isAdmin && adminApiKey) {
          headers['x-admin-key'] = adminApiKey;
        } else if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
        
        const response = await fetch('/api/upload/product-image', {
          method: 'POST',
          headers,
          body: formData
        });
        
        if (response.ok) {
          const data = await response.json();
          return data.imageUrl;
        }
        throw new Error('Upload failed');
      });
      
      const uploadedUrls = await Promise.all(uploadPromises);
      setFormData(prev => ({
        ...prev,
        product_images: [...prev.product_images, ...uploadedUrls]
      }));
      setImagePreview(prev => [...prev, ...uploadedUrls]);
    } catch (error) {
      console.error('Image upload error:', error);
      alert('Failed to upload images');
    } finally {
      setUploadingImages(false);
    }
  };

  // Remove image from product
  const removeImage = (index) => {
    setFormData(prev => ({
      ...prev,
      product_images: prev.product_images.filter((_, i) => i !== index)
    }));
    setImagePreview(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Validate form before submission
      if (!validateForm()) {
        alert('Please fix the validation errors before submitting.');
        return;
      }
      
      let token = localStorage.getItem('token');
      let adminApiKey = localStorage.getItem('adminApiKey');
      
      // Only treat as admin when userType is admin AND adminApiKey is present
      const isAdmin = localStorage.getItem('userType') === 'admin' && Boolean(adminApiKey);
      
      if (!token && !isAdmin) {
        try {
          const v = JSON.parse(localStorage.getItem('vendorData') || 'null');
          token = v?.token || '';
        } catch (_) {}
      }
      
      const headers = {
        'Content-Type': 'application/json'
      };
      if (isAdmin && adminApiKey) {
        headers['x-admin-key'] = adminApiKey;
      } else if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      // Admin or vendor add/edit: send through products endpoints (backend handles inventory fields too)
      const baseEndpoint = isAdmin ? '/api/admin/products' : '/api/products';
      const url = editingProduct ? `${baseEndpoint}/${editingProduct.id}` : baseEndpoint;
      const method = editingProduct ? 'PUT' : 'POST';
      const payload = {
        ...formData,
        // Ensure numeric types are numbers
        price: Number(formData.price !== '' ? formData.price : (formData.cost_price || 0)),
        cost_price: formData.cost_price !== '' ? Number(formData.cost_price) : null,
        gst_slab: formData.gst_slab !== '' ? Number(formData.gst_slab) : null,
        stock_on_hand: formData.stock_on_hand !== '' ? Number(formData.stock_on_hand) : 0,
        min_stock_level: formData.min_stock_level !== '' ? Number(formData.min_stock_level) : 0,
        reorder_point: formData.reorder_point !== '' ? Number(formData.reorder_point) : 0,
        reorder_quantity: formData.reorder_quantity !== '' ? Number(formData.reorder_quantity) : 0,
        category_id: formData.category_id !== '' ? Number(formData.category_id) : null
      };
      const response = await fetch(url, { method, headers, body: JSON.stringify(payload) });
      
      if (response.ok) {
        alert(editingProduct ? 'Product updated successfully' : 'Product added successfully');
        setShowModal(false);
        setEditingProduct(null);
        setFormData({
          name: '',
          description: '',
          sku: '',
          category: '',
          category_id: '',
          price: '',
          cost_price: '',
          image_url: '',
          product_images: [],
          unit: 'piece',
          weight: '',
          hsn_code: '',
          gst_slab: '',
          stock_on_hand: 0,
          min_stock_level: 0,
          reorder_point: 0,
          reorder_quantity: 0,
          vendor_id: ''
        });
        setValidationErrors({});
        setImagePreview([]);
        fetchProducts();
      } else if (response.status === 401) {
        alert('Your session has expired. Please log in again.');
        try { localStorage.removeItem('token'); localStorage.removeItem('vendorData'); localStorage.setItem('isLoggedIn','false'); } catch (_) {}
        window.location.reload();
      } else {
        try {
          const errData = await response.json();
          alert(errData.error || 'Failed to save product');
        } catch (_) {
          alert('Failed to save product');
        }
      }
    } catch (error) {
      console.error('Error saving product:', error);
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    let productImages = [];
    try {
      if (Array.isArray(product.product_images)) {
        productImages = product.product_images;
      } else if (typeof product.product_images === 'string' && product.product_images.trim()) {
        const parsed = JSON.parse(product.product_images);
        productImages = Array.isArray(parsed) ? parsed : [];
      }
    } catch (_) {
      productImages = [];
    }
    if (productImages.length === 0 && product.image_url) {
      productImages = [product.image_url];
    }
    setFormData({
      name: product.name,
      description: product.description || '',
      sku: product.sku,
      category: product.category,
      category_id: (typeof product.category_id !== 'undefined' && product.category_id !== null) ? String(product.category_id) : '',
      price: product.price || '',
      cost_price: product.cost_price || '',
      image_url: product.image_url || '',
      product_images: productImages,
      unit: product.unit || 'piece',
      weight: product.weight || '',
      hsn_code: product.hsn_code || '',
      gst_slab: (product.gst_slab !== null && product.gst_slab !== undefined && product.gst_slab !== '') ? String(product.gst_slab) : '',
      stock_on_hand: (typeof product.stock_available !== 'undefined' ? product.stock_available : (product.stock_on_hand || 0)),
      min_stock_level: product.min_stock_level || 0,
      reorder_point: product.reorder_point || 0,
      reorder_quantity: product.reorder_quantity || 0,
      vendor_id: product.vendor_id || ''
    });
    setValidationErrors({});
    setImagePreview(productImages);
    setShowModal(true);
  };

  const toggleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(products.map(p => p.id));
    } else {
      setSelectedIds([]);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handleBulkSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedIds.length === 0) { alert('Select at least one product'); return; }

      let token = localStorage.getItem('token');
      let adminApiKey = localStorage.getItem('adminApiKey');
      const isAdmin = localStorage.getItem('userType') === 'admin' && Boolean(adminApiKey);
      if (!token && !isAdmin) {
        try { const v = JSON.parse(localStorage.getItem('vendorData') || 'null'); token = v?.token || ''; } catch (_) {}
      }
      const headers = { 'Content-Type': 'application/json' };
      if (isAdmin && adminApiKey) headers['x-admin-key'] = adminApiKey; else if (token) headers['Authorization'] = `Bearer ${token}`;

      // For vendors: send price requests instead of direct price updates
      const doPrice = bulkData.priceDeltaPercent || bulkData.newPrice;
      const doStock = bulkData.stockDelta || bulkData.newStock;

      for (const id of selectedIds) {
        const product = products.find(p => p.id === id);
        if (!product) continue;

        // Stock updates (direct PUT allowed for vendor products)
        if (doStock) {
          const newStockOnHand = (() => {
            const cur = Number((typeof product.stock_available !== 'undefined' ? product.stock_available : (product.stock_on_hand || 0)) || 0);
            if (bulkData.newStock !== '') return Number(bulkData.newStock);
            const delta = Number(bulkData.stockDelta || 0);
            return Math.max(0, cur + delta);
          })();
          if (isAdmin) {
            // Keep admin logic as-is for now (admin products endpoint)
            await fetch(`/api/admin/products/${id}`, { method: 'PUT', headers, body: JSON.stringify({ stock_on_hand: newStockOnHand }) });
          } else {
            const vendorId = (() => { try { return JSON.parse(localStorage.getItem('vendorData') || '{}').id; } catch (_) { return null; } })();
            if (!vendorId) throw new Error('Missing vendorId');
            const invHeaders = { ...headers, 'Content-Type': 'application/json' };
            await fetch(`/api/inventory/vendor/${vendorId}/product/${id}/stock`, {
              method: 'PUT',
              headers: invHeaders,
              body: JSON.stringify({ stockOnHand: Number(newStockOnHand), reason: 'bulk_adjustment' })
            });
          }
        }

        // Price updates
        if (doPrice) {
          if (isAdmin) {
            const current = Number(product.price || 0);
            const newPrice = (bulkData.newPrice !== '') ? Number(bulkData.newPrice) : Number((current * (1 + Number(bulkData.priceDeltaPercent) / 100)).toFixed(2));
            await fetch(`/api/admin/products/${id}/price`, { method: 'PUT', headers, body: JSON.stringify({ new_price: newPrice, reason: 'Bulk update' }) });
          } else {
            const current = Number(product.price || 0);
            const requested = (bulkData.newPrice !== '') ? Number(bulkData.newPrice) : Number((current * (1 + Number(bulkData.priceDeltaPercent) / 100)).toFixed(2));
            await fetch(`/api/products/${id}/price-request`, { method: 'POST', headers, body: JSON.stringify({ requested_price: requested, reason: 'Bulk update' }) });
          }
        }
      }

      setShowBulkModal(false);
      setBulkData({ priceDeltaPercent: '', newPrice: '', stockDelta: '', newStock: '' });
      setSelectedIds([]);
      fetchProducts();
    } catch (e) {
      console.error('Bulk update error:', e);
      alert('Bulk update failed');
    }
  };

  const handleDelete = async (productId) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        let token = localStorage.getItem('token');
        let adminApiKey = localStorage.getItem('adminApiKey');
        
        // Check admin context strictly
        const isAdmin = localStorage.getItem('userType') === 'admin' && Boolean(adminApiKey);
        
        if (!token && !isAdmin) {
          try {
            const v = JSON.parse(localStorage.getItem('vendorData') || 'null');
            token = v?.token || '';
          } catch (_) {}
        }
        
        const headers = {};
        if (isAdmin && adminApiKey) {
          headers['x-admin-key'] = adminApiKey;
        } else if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
        
        const endpoint = isAdmin ? '/api/admin/products' : '/api/products';
        const response = await fetch(`${endpoint}/${productId}`, {
          method: 'DELETE',
          headers
        });
        
        if (response.ok) {
          fetchProducts();
        }
      } catch (error) {
        console.error('Error deleting product:', error);
      }
    }
  };

  const handleCSVUpload = async (e) => {
    e.preventDefault();
    if (!csvFile) return;
    
    try {
      setUploading(true);
      let token = localStorage.getItem('token');
      let adminApiKey = localStorage.getItem('adminApiKey');
      
      // Only treat as admin when userType is admin AND adminApiKey is present
      const isAdmin = localStorage.getItem('userType') === 'admin' && Boolean(adminApiKey);
      
      if (!token && !isAdmin) {
        try {
          const v = JSON.parse(localStorage.getItem('vendorData') || 'null');
          token = v?.token || '';
        } catch (_) {}
      }
      
      const headers = {};
      if (isAdmin && adminApiKey) {
        headers['x-admin-key'] = adminApiKey;
      } else if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const formData = new FormData();
      formData.append('csvFile', csvFile);
      
      const endpoint = isAdmin ? '/api/admin/products/upload-csv' : '/api/products/upload-csv';
      const response = await fetch(endpoint, {
        method: 'POST',
        headers,
        body: formData
      });
      
      if (response.ok) {
        const result = await response.json();
        setUploadResult(result);
        fetchProducts();
      } else if (response.status === 401) {
        alert('Your session has expired. Please log in again.');
        try { localStorage.removeItem('token'); localStorage.removeItem('vendorData'); localStorage.setItem('isLoggedIn','false'); } catch (_) {}
        window.location.reload();
      }
    } catch (error) {
      console.error('Error uploading CSV:', error);
    } finally {
      setUploading(false);
    }
  };

  const downloadTemplate = async () => {
    try {
      const response = await fetch('/api/products/csv-template');
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'product_template.csv';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    } catch (error) {
      console.error('Error downloading template:', error);
    }
  };

  const getStatusBadge = (product) => {
    if (product.stock_available <= 0) return 'status-out-of-stock';
    if (product.stock_available <= product.min_stock_level) return 'status-low-stock';
    if (product.stock_available <= product.reorder_point) return 'status-critical-stock';
    return 'status-active';
  };

  const getStockStatus = (product) => {
    if (product.stock_available <= 0) return 'Out of Stock';
    if (product.stock_available <= product.min_stock_level) return 'Low Stock';
    if (product.stock_available <= product.reorder_point) return 'Reorder Point';
    return 'In Stock';
  };

  const openDetails = async (p) => {
    try {
      // Try to load the freshest copy from API (includes inventory join)
      let token = localStorage.getItem('token');
      if (!token) {
        try {
          const v = JSON.parse(localStorage.getItem('vendorData') || 'null');
          token = v?.token || '';
        } catch (_) {}
      }
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      const res = await fetch(`/api/products/${p.id}`, { headers });
      if (res.ok) {
        const fresh = await res.json();
        setDetailsProduct(fresh);
      } else {
        // Fallback to the item we already have
        setDetailsProduct(p);
      }
      setShowDetails(true);
    } catch (_) {
      setDetailsProduct(p);
      setShowDetails(true);
    }
  };

  return (
    <div className="products">
      <div className="products-header">
        <div className="header-content">
          <h1>Product Management</h1>
          <p>Manage your product catalog and inventory</p>
        </div>
      </div>

      <div className="products-filters">
        <div className="filters-left">
          <div className="search-container">
            <input
              type="text"
              placeholder="Search products..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
          </div>
          
          <div className="filter-group">
            <LucideIcons.Filter size={20} />
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Categories</option>
              {categories.map(category => (
                <option key={category.id} value={category.name}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="lowstock">Low Stock</option>
              <option value="criticalstock">Critical Stock</option>
              <option value="outofstock">Out of Stock</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          <div className="filter-group">
            <button className="btn btn-secondary" onClick={() => setViewMode(viewMode === 'grid' ? 'table' : 'grid')}>
              {viewMode === 'grid' ? 'Table View' : 'Card View'}
            </button>
          </div>
        </div>

        <div className="filters-right">
          <div className="results-count">
            {products.length} products
          </div>
          <button className="btn btn-primary" onClick={openAddProduct}>
            <LucideIcons.Plus size={20} />
            Add Products
          </button>
          {/* Bulk Update button removed as requested */}
        </div>
      </div>

      {loading ? (
        <div className="loading">Loading products...</div>
      ) : (
        <>
      {viewMode === 'grid' ? (
        <div className="products-grid">
          {products.map((product) => (
          <div key={product.id} className="product-card">
            <div className="product-image">
              {(() => {
                // Try to get images from product_images first, then fallback to image_url
                let images = [];
                if (product.product_images) {
                  try {
                    images = Array.isArray(product.product_images)
                      ? product.product_images
                      : JSON.parse(product.product_images);
                  } catch (e) {
                    images = [];
                  }
                }
                if (images.length === 0 && product.image_url) {
                  images = [product.image_url];
                }
                
                if (images.length > 0) {
                  return (
                    <div className="product-image-container">
                      <img src={images[0]} alt={product.name} />
                      {images.length > 1 && (
                        <div className="image-count-badge">
                          +{images.length - 1}
                        </div>
                      )}
                    </div>
                  );
                }
                return <span className="product-emoji">📦</span>;
              })()}
            </div>
            <div className="product-info">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                <h3 className="product-name" style={{ marginBottom: 0 }}>{product.name}</h3>
                <input type="checkbox" checked={selectedIds.includes(product.id)} onChange={() => toggleSelect(product.id)} />
              </div>
              <p className="product-category">{product.category}</p>
              <p className="product-id" style={{ fontWeight: 'bold', color: '#2563eb' }}>Product ID: {product.product_id || 'N/A'}</p>
              <p className="product-sku">SKU: {product.sku}</p>
              {product.vendor_name && (
                <p className="product-vendor">Vendor: {product.vendor_name}</p>
              )}
              <p className="product-description">{product.description}</p>
              <div className="product-pricing">
                <div className="price-info">
                  <div className="cost-price">Cost: {product.cost_price ? formatINR(product.cost_price) : 'N/A'}</div>
                  <div className="platform-price">Platform Price: {formatINR(product.price)}</div>
                </div>
              </div>
              <div className="product-stock">
                    <div className="stock-info">
                      <span className="stock-label">Available:</span>
                      <span className={`stock-value ${getStatusBadge(product)}`}>
                        {product.stock_available || 0}
                </span>
                    </div>
                    <div className="stock-info">
                      <span className="stock-label">Reserved:</span>
                      <span className="stock-value">{product.stock_reserved || 0}</span>
                    </div>
                    <div className="stock-info">
                      <span className="stock-label">Min Level:</span>
                      <span className="stock-value">{product.min_stock_level || 0}</span>
                    </div>
              </div>
              <div className="product-status">
                    <span className={`status-badge ${getStatusBadge(product)}`}>
                      {getStockStatus(product)}
                </span>
              </div>
            </div>
            <div className="product-actions">
              <button className="action-btn" title="View Details" onClick={() => openDetails(product)}>
                <LucideIcons.Eye size={16} />
              </button>
                  <button className="action-btn" title="Edit Product" onClick={() => handleEdit(product)}>
                <LucideIcons.Edit size={16} />
              </button>
                  <button className="action-btn" title="Request Price Change" onClick={() => { setPriceRequestProduct(product); setRequestedPrice(product.price); setShowPriceRequest(true); }}>
                <LucideIcons.AlertTriangle size={16} />
              </button>
                  <button className="action-btn delete" title="Delete Product" onClick={() => handleDelete(product.id)}>
                <LucideIcons.Trash2 size={16} />
              </button>
            </div>
          </div>
        ))}
        </div>
      ) : (
        <div className="table-wrapper">
          <table className="products-table">
            <thead>
              <tr>
                <th>Product Name</th>
                <th>Product ID</th>
                <th>SKU</th>
                <th>Category</th>
                <th>Cost Price</th>
                <th>Price</th>
                <th>GST</th>
                <th>Stock</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {products.map(p => (
                <tr key={p.id}>
                  <td>{p.name}</td>
                  <td><span style={{ fontWeight: 'bold', color: '#2563eb' }}>{p.product_id || 'N/A'}</span></td>
                  <td>{p.sku}</td>
                  <td>{p.category}</td>
                  <td>{p.cost_price ? formatINR(p.cost_price) : 'N/A'}</td>
                  <td>{formatINR(p.price)}</td>
                  <td>{(p.gst_slab !== null && p.gst_slab !== undefined && p.gst_slab !== '') ? `${p.gst_slab}%` : '-'}</td>
                  <td>{p.stock_available || 0}</td>
                  <td><span className={`status-badge ${getStatusBadge(p)}`}>{getStockStatus(p)}</span></td>
                  <td>
                    <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                      <button className="action-btn" title="View Details" onClick={() => openDetails(p)}><LucideIcons.Eye size={16} /></button>
                      <button className="action-btn" title="Edit" onClick={() => handleEdit(p)}><LucideIcons.Edit size={16} /></button>
                      <button className="action-btn" title="Price" onClick={() => { setViewMode('table'); setPriceRequestProduct(p); setRequestedPrice(p.price); setShowPriceRequest(true); }}><LucideIcons.AlertTriangle size={16} /></button>
                      <button className="action-btn delete" title="Delete" onClick={() => handleDelete(p.id)}><LucideIcons.Trash2 size={16} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

          {totalPages > 1 && (
            <div className="pagination">
              <button 
                className="btn btn-secondary" 
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
              >
                Previous
              </button>
              <span className="page-info">
                Page {currentPage} of {totalPages}
              </span>
              <button 
                className="btn btn-secondary" 
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(currentPage + 1)}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {/* Add/Edit Product Modal */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>{editingProduct ? 'Edit Product' : 'Add New Product'}</h2>
              <button className="close-btn" onClick={() => setShowModal(false)}>
                <LucideIcons.X size={20} />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="product-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Product Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className={validationErrors.name ? 'error' : ''}
                  />
                  {validationErrors.name && <div className="error-message">{validationErrors.name}</div>}
                </div>
                <div className="form-group">
                  <label>SKU *</label>
                  <input
                    type="text"
                    name="sku"
                    value={formData.sku}
                    onChange={handleInputChange}
                    required
                    className={validationErrors.sku ? 'error' : ''}
                  />
                  <small>SKU is automatically generated in format: SONI-[VendorID]-[CategoryCode]-[0001]</small>
                  {validationErrors.sku && <div className="error-message">{validationErrors.sku}</div>}
                </div>
              </div>

              {vendors.length > 0 && (
                <div className="form-row">
                  <div className="form-group">
                    <label>Vendor *</label>
                    <select
                      name="vendor_id"
                      value={formData.vendor_id}
                      onChange={handleInputChange}
                      required
                      className={validationErrors.vendor_id ? 'error' : ''}
                    >
                      <option value="">Select Vendor</option>
                      {vendors.map((vendor) => (
                        <option key={vendor.id} value={vendor.id}>
                          {vendor.shop_name || vendor.owner_name} ({vendor.email})
                        </option>
                      ))}
                    </select>
                    {validationErrors.vendor_id && <div className="error-message">{validationErrors.vendor_id}</div>}
                  </div>
                </div>
              )}

              <div className="form-row">
                <div className="form-group">
                  <label>Category *</label>
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleCategoryChange}
                    required
                    className={validationErrors.category ? 'error' : ''}
                  >
                    <option value="">Select Category</option>
                    {categories.map(category => (
                      <option key={category.id} value={category.name}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                  {validationErrors.category && <div className="error-message">{validationErrors.category}</div>}
                </div>
                <div className="form-group">
                  <label>Base Cost Price *</label>
                  <input
                    type="number"
                    step="0.01"
                    name="cost_price"
                    value={formData.cost_price}
                    onChange={handleInputChange}
                    required
                    className={validationErrors.cost_price ? 'error' : ''}
                  />
                  <small>Your cost price. Final customer price is determined by platform rules.</small>
                  {validationErrors.cost_price && <div className="error-message">{validationErrors.cost_price}</div>}
                </div>
              </div>

            <div className="form-row">
              <div className="form-group">
                <label>GST Slab (%)</label>
                <select
                  name="gst_slab"
                  value={formData.gst_slab}
                  onChange={handleInputChange}
                  className={validationErrors.gst_slab ? 'error' : ''}
                >
                  <option value="">Select GST Rate</option>
                  {gstRates.map(gstRate => (
                    <option key={gstRate.rate} value={String(gstRate.rate)}>
                      {gstRate.display}
                    </option>
                  ))}
                </select>
                <small>GST rate is automatically set based on selected category</small>
                {validationErrors.gst_slab && <div className="error-message">{validationErrors.gst_slab}</div>}
              </div>
            </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows="3"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Unit *</label>
                  <select
                    name="unit"
                    value={formData.unit}
                    onChange={handleInputChange}
                    required
                    className={validationErrors.unit ? 'error' : ''}
                  >
                    <option value="">Select Unit</option>
                    <option value="piece">Piece</option>
                    <option value="kg">Kilogram</option>
                    <option value="g">Gram</option>
                    <option value="l">Liter</option>
                    <option value="ml">Milliliter</option>
                    <option value="box">Box</option>
                    <option value="pack">Pack</option>
                  </select>
                  {validationErrors.unit && <div className="error-message">{validationErrors.unit}</div>}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Weight *</label>
                  <input
                    type="text"
                    name="weight"
                    value={formData.weight}
                    onChange={handleInputChange}
                    placeholder="e.g., 500g, 1kg, 2.5lbs"
                    required
                    className={validationErrors.weight ? 'error' : ''}
                  />
                  {validationErrors.weight && <div className="error-message">{validationErrors.weight}</div>}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>HSN Code {formData.category && ['Food & Beverages','Dairy','Bakery','Pantry'].includes(formData.category) ? '(required)' : ''}</label>
                  <input
                    type="text"
                    name="hsn_code"
                    value={formData.hsn_code}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9]/g, ''); // Only allow digits
                      if (value.length <= 5) { // Limit to 5 digits
                        setFormData({ ...formData, hsn_code: value });
                      }
                    }}
                    className={validationErrors.hsn_code ? 'error' : ''}
                    maxLength="5"
                    placeholder="Enter 3-5 digit HSN code"
                  />
                  <small>HSN code is automatically set based on selected category</small>
                  {validationErrors.hsn_code && <div className="error-message">{validationErrors.hsn_code}</div>}
                </div>
              </div>

              {/* Product Images Section */}
              <div className="form-group">
                <label>Product Images</label>
                <div className="image-upload-section">
                  <div className="image-upload-area">
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={handleImageUpload}
                      disabled={uploadingImages}
                      className="image-upload-input"
                      id="product-images"
                    />
                    <label htmlFor="product-images" className="image-upload-label">
                      {uploadingImages ? (
                        <div className="uploading">
                          <LucideIcons.Loader2 size={20} className="spinning" />
                          <span>Uploading...</span>
                        </div>
                      ) : (
                        <div className="upload-prompt">
                          <LucideIcons.Upload size={24} />
                          <span>Click to upload images</span>
                          <small>Supports multiple images (JPG, PNG, GIF)</small>
                        </div>
                      )}
                    </label>
                  </div>
                  
                  {/* Image Preview */}
                  {imagePreview.length > 0 && (
                    <div className="image-preview-grid">
                      {imagePreview.map((imageUrl, index) => (
                        <div key={index} className="image-preview-item">
                          <img src={imageUrl} alt={`Product ${index + 1}`} />
                          <button
                            type="button"
                            className="remove-image-btn"
                            onClick={() => removeImage(index)}
                          >
                            <LucideIcons.X size={16} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Legacy Image URL (for backward compatibility) */}
              <div className="form-group">
                <label>Image URL (Legacy)</label>
                <input
                  type="url"
                  name="image_url"
                  value={formData.image_url}
                  onChange={handleInputChange}
                  placeholder="Single image URL (use image upload above for multiple images)"
                />
                <small>This field is for backward compatibility. Use the image upload above for better functionality.</small>
              </div>

              <div className="inventory-section">
                <h3>Inventory Settings</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label>Stock On Hand *</label>
                    <input
                      type="number"
                      name="stock_on_hand"
                      value={formData.stock_on_hand}
                      onChange={handleInputChange}
                      min="0"
                      required
                      className={validationErrors.stock_on_hand ? 'error' : ''}
                    />
                    {validationErrors.stock_on_hand && <div className="error-message">{validationErrors.stock_on_hand}</div>}
                  </div>
                  <div className="form-group">
                    <label>Min Stock Level *</label>
                    <input
                      type="number"
                      name="min_stock_level"
                      value={formData.min_stock_level}
                      onChange={handleInputChange}
                      min="0"
                      required
                      className={validationErrors.min_stock_level ? 'error' : ''}
                    />
                    {validationErrors.min_stock_level && <div className="error-message">{validationErrors.min_stock_level}</div>}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Reorder Point *</label>
                    <input
                      type="number"
                      name="reorder_point"
                      value={formData.reorder_point}
                      onChange={handleInputChange}
                      min="0"
                      required
                      className={validationErrors.reorder_point ? 'error' : ''}
                    />
                    {validationErrors.reorder_point && <div className="error-message">{validationErrors.reorder_point}</div>}
                  </div>
                </div>

                <div className="form-group">
                  <label>Reorder Quantity *</label>
                  <input
                    type="number"
                    name="reorder_quantity"
                    value={formData.reorder_quantity}
                    onChange={handleInputChange}
                    min="0"
                    required
                    className={validationErrors.reorder_quantity ? 'error' : ''}
                  />
                  {validationErrors.reorder_quantity && <div className="error-message">{validationErrors.reorder_quantity}</div>}
                </div>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingProduct ? 'Update Product' : 'Add Product'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* CSV Upload Modal */}
      {showCSVModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>Upload Products CSV</h2>
              <button className="close-btn" onClick={() => setShowCSVModal(false)}>
                <LucideIcons.X size={20} />
              </button>
            </div>
            <form onSubmit={handleCSVUpload} className="csv-upload-form">
              <div className="form-group">
                <label>Select CSV File</label>
                <input
                  type="file"
                  accept=".csv"
                  onChange={(e) => setCsvFile(e.target.files[0])}
                  required
                />
                <p className="help-text">
                  Download the template to see the required format
                </p>
              </div>

              {uploadResult && (
                <div className="upload-result">
                  <h4>Upload Results</h4>
                  <div className="result-stats">
                    <div className="stat">
                      <span className="stat-label">Total Rows:</span>
                      <span className="stat-value">{uploadResult.totalRows}</span>
                    </div>
                    <div className="stat success">
                      <span className="stat-label">Successful:</span>
                      <span className="stat-value">{uploadResult.successfulRows}</span>
                    </div>
                    <div className="stat error">
                      <span className="stat-label">Failed:</span>
                      <span className="stat-value">{uploadResult.failedRows}</span>
                    </div>
                  </div>
                  {uploadResult.errors && uploadResult.errors.length > 0 && (
                    <div className="error-list">
                      <h5>Errors:</h5>
                      <ul>
                        {uploadResult.errors.map((error, index) => (
                          <li key={index}>{error}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowCSVModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={!csvFile || uploading}>
                  {uploading ? 'Uploading...' : 'Upload CSV'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showBulkModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>Bulk Update</h2>
              <button className="close-btn" onClick={() => setShowBulkModal(false)}>
                <LucideIcons.X size={20} />
              </button>
            </div>
            <form onSubmit={handleBulkSubmit} className="product-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Price Change (%)</label>
                  <input type="number" step="0.01" value={bulkData.priceDeltaPercent} onChange={(e) => setBulkData({ ...bulkData, priceDeltaPercent: e.target.value })} placeholder="e.g. 5 for +5% or -5 for -5%" />
                </div>
                <div className="form-group">
                  <label>Set New Price</label>
                  <input type="number" step="0.01" value={bulkData.newPrice} onChange={(e) => setBulkData({ ...bulkData, newPrice: e.target.value })} placeholder="Overrides % if provided" />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Stock Delta (±)</label>
                  <input type="number" step="1" value={bulkData.stockDelta} onChange={(e) => setBulkData({ ...bulkData, stockDelta: e.target.value })} placeholder="e.g. 10 or -5" />
                </div>
                <div className="form-group">
                  <label>Set New Stock</label>
                  <input type="number" step="1" value={bulkData.newStock} onChange={(e) => setBulkData({ ...bulkData, newStock: e.target.value })} placeholder="Overrides delta if provided" />
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowBulkModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary">Apply to {selectedIds.length} items</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showPriceRequest && priceRequestProduct && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>Request Price Change</h2>
              <button className="close-btn" onClick={() => setShowPriceRequest(false)}>
              <LucideIcons.X size={20} />
              </button>
            </div>
            <div className="product-form" style={{ display: 'grid', gap: 12 }}>
              <div>
                <strong>{priceRequestProduct.name}</strong> (SKU: {priceRequestProduct.sku})
              </div>
              <label>
                Requested Price
                <input type="number" step="0.01" value={requestedPrice} onChange={(e) => setRequestedPrice(e.target.value)} />
              </label>
              <label>
                Reason
                <textarea rows="3" value={requestReason} onChange={(e) => setRequestReason(e.target.value)} />
              </label>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowPriceRequest(false)}>
                  Cancel
                </button>
                <button type="button" className="btn btn-primary" onClick={async () => {
                  try {
                    let token = localStorage.getItem('token');
                    let adminApiKey = localStorage.getItem('adminApiKey');
                    
                    // Check admin context strictly
                    const isAdmin = localStorage.getItem('userType') === 'admin' && Boolean(adminApiKey);
                    
                    if (!token && !isAdmin) {
                      try {
                        const v = JSON.parse(localStorage.getItem('vendorData') || 'null');
                        token = v?.token || '';
                      } catch (_) {}
                    }
                    
                    const headers = { 'Content-Type': 'application/json' };
                    if (isAdmin && adminApiKey) {
                      headers['x-admin-key'] = adminApiKey;
                    } else if (token) {
                      headers['Authorization'] = `Bearer ${token}`;
                    }
                    
                    const res = await fetch(`/api/products/${priceRequestProduct.id}/price-request`, {
                      method: 'POST',
                      headers,
                      body: JSON.stringify({ requested_price: requestedPrice, reason: requestReason })
                    });
                    if (res.ok) { setShowPriceRequest(false); setRequestReason(''); }
                  } catch (e) {}
                }}>
                  Submit Request
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showDetails && detailsProduct && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>Product Details</h2>
              <button className="close-btn" onClick={() => setShowDetails(false)}>
                <LucideIcons.X size={20} />
              </button>
            </div>
            <div className="product-form" style={{ display: 'grid', gap: 12 }}>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                {(() => {
                  let images = [];
                  try {
                    if (detailsProduct.product_images) {
                      images = Array.isArray(detailsProduct.product_images) ? detailsProduct.product_images : JSON.parse(detailsProduct.product_images || '[]');
                    }
                  } catch (_) { images = []; }
                  if (images.length === 0 && detailsProduct.image_url) images = [detailsProduct.image_url];
                  if (images.length > 0) {
                    return (
                      <div className="product-image-container">
                        <img src={images[0]} alt={detailsProduct.name} style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 8 }} />
                      </div>
                    );
                  }
                  return <span className="product-emoji">📦</span>;
                })()}
                <div>
                  <h3 style={{ margin: 0 }}>{detailsProduct.name}</h3>
                  <div style={{ color: '#2563eb', fontWeight: 'bold' }}>Product ID: {detailsProduct.product_id || 'N/A'}</div>
                  <div style={{ color: '#666' }}>SKU: {detailsProduct.sku}</div>
                  <div style={{ color: '#666' }}>{detailsProduct.category}</div>
                </div>
              </div>
              <div>
                <strong>Description</strong>
                <div style={{ whiteSpace: 'pre-wrap' }}>{detailsProduct.description || '-'}</div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
                <div>
                  <div className="stock-label">Platform Price</div>
                  <div className="stock-value">{formatINR(detailsProduct.price)}</div>
                </div>
                <div>
                  <div className="stock-label">Cost</div>
                  <div className="stock-value">{detailsProduct.cost_price ? formatINR(detailsProduct.cost_price) : 'N/A'}</div>
                </div>
                <div>
                  <div className="stock-label">GST Slab</div>
                  <div className="stock-value">{detailsProduct.gst_slab ?? '-'}</div>
                </div>
                <div>
                  <div className="stock-label">HSN</div>
                  <div className="stock-value">{detailsProduct.hsn_code || '-'}</div>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12 }}>
                <div>
                  <div className="stock-label">Available</div>
                  <div className={`stock-value ${getStatusBadge(detailsProduct)}`}>{detailsProduct.stock_available || 0}</div>
                </div>
                <div>
                  <div className="stock-label">Reserved</div>
                  <div className="stock-value">{detailsProduct.stock_reserved || 0}</div>
                </div>
                <div>
                  <div className="stock-label">Min Level</div>
                  <div className="stock-value">{detailsProduct.min_stock_level || 0}</div>
                </div>
                <div>
                  <div className="stock-label">Reorder Point</div>
                  <div className="stock-value">{detailsProduct.reorder_point || 0}</div>
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowDetails(false)}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Products;
